import { Box, Button, Modal, RadioGroup, RadioGroupProps, SpaceBetween } from '@amzn/awsui-components-react';
import { unparse } from 'papaparse';
import { useEffect, useState } from 'react';

import { getFormattedDateAndTime } from '../util';
import { localization } from './constants';
import { getCountText } from './helper';
import { ColumnDefinition } from './types';

enum DownloadOptions {
    DOWNLOAD_ALL = 'downloadAll',
    DOWNLOAD_FILTERED = 'downloadFiltered',
    DOWNLOAD_SELECTED = 'downloadSelected',
    DOWNLOAD_SINGLE_PAGE = 'downloadSinglePage',
}
interface Props<T> {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    includedColumns: readonly string[] | undefined;
    columnDefinition: ColumnDefinition<T>[];
    singlePageItems: readonly T[];
    allItems: readonly T[];
    filteredItems: readonly T[];
    selectedItems: readonly T[];
    fileName?: string;
}
const defaultDownloadOptions: RadioGroupProps.RadioButtonDefinition[] = [
    {
        value: DownloadOptions.DOWNLOAD_ALL,
        label: localization.DOWNLOAD_ALL,
        description: localization.DOWNLOAD_ALL_DESCRIPTION,
    }
];

const DownloadModal = <T,>(props: Props<T>): JSX.Element => {
    const [ downloadOptions, setDownloadOptions ] = useState(defaultDownloadOptions);
    const [ selectedOption, setSelectedOption ] = useState(downloadOptions[0].value);
    const [ downloading, setDownloading ] = useState(false);

    useEffect(() => {
        const options = [
            {
                value: DownloadOptions.DOWNLOAD_ALL,
                label: getCountText(0, 0, props.allItems.length),
                description: localization.DOWNLOAD_ALL_DESCRIPTION,
            }
        ];
        if (props.filteredItems.length !== 0) {
            options.push(
                {
                    value: DownloadOptions.DOWNLOAD_FILTERED,
                    label: getCountText(0, props.filteredItems.length, 0),
                    description: localization.DOWNLOAD_FILTERED_DESCRIPTION,
                }
            );
        }
        if (props.selectedItems.length !== 0) {
            options.push(
                {
                    value: DownloadOptions.DOWNLOAD_SELECTED,
                    label: getCountText(props.selectedItems.length, 0, 0),
                    description: localization.DOWNLOAD_SELECTED_DESCRIPTION,
                }
            );
        }
        options.push(
            {
                value: DownloadOptions.DOWNLOAD_SINGLE_PAGE,
                label: localization.DOWNLOAD_SINGLE_PAGE,
                description: localization.DOWNLOAD_SINGLE_PAGE_DESCRIPTION,
            }
        );
        setDownloadOptions(options);
    }, [ props.filteredItems.length, props.allItems.length, props.selectedItems.length ]);

    const download = () => {
        setDownloading(true);
        const columns = props.includedColumns ? [...props.includedColumns] : undefined;
        let items: T[] = [];
        if (selectedOption === DownloadOptions.DOWNLOAD_ALL) {
            items = [...props.allItems];
        } else if (selectedOption === DownloadOptions.DOWNLOAD_FILTERED) {
            items = [...props.filteredItems];
        } else if (selectedOption === DownloadOptions.DOWNLOAD_SELECTED) {
            items = [...props.selectedItems];
        } else {
            items = [...props.singlePageItems];
        }
        
        // column header: header values (external) are different from the column ids (internal)
        // columns holds the visible columns, hence using those for the download as well.
        const columnHeaders = columns?.map(columnId => {
            const columnDefinition = props.columnDefinition.find(col => col.id === columnId);
            return (columnDefinition?.text || columnDefinition?.header?.toString() || columnId);
        }) || [];

        const colDefs = new Map();
        columns?.map(columnId => {
            colDefs.set(columnId, props.columnDefinition.find(col => col.id === columnId));
        });

        const rows: string[][] = items.map((item: T) => columns?.map(columnId => {
            const columnDefinition = colDefs.get(columnId);
            if (columnDefinition.data) {
                return columnDefinition.data(item);
            } else {
                return item[columnId as keyof T];
            }
        }) || []);

        // constructing the csv explicitly from headers and rows of data
        // for reference on unparse using fields and data, see https://www.papaparse.com/docs#unparse
        const csvData = unparse({ fields: columnHeaders, data: rows});
        const fileName = `${(props.fileName?.replace(/ /g, '_') || localization.DOWNLOAD_FILE_NAME)}_` +
            `${getFormattedDateAndTime()}${localization.DOWNLOAD_FILE_CSV_EXTENSION}`;

        const blob = new Blob([csvData], { type: 'attachment/csv' });
        const csvUrl = window.URL.createObjectURL(blob);
        const tempLink = document.createElement('a');
        tempLink.href = csvUrl;
        tempLink.setAttribute('download', fileName);
        tempLink.click();
            
        setDownloading(false);
        props.setVisible(false);

    };
    return (
        <Modal
            onDismiss={() => props.setVisible(false)}
            visible={props.visible}
            size="medium"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={() => props.setVisible(false)}>{localization.CANCEL_BUTTON_TEXT}</Button>
                        <Button variant="primary" loading={downloading} onClick={download}>{localization.DOWNLOAD_BUTTON_TEXT}</Button>
                    </SpaceBetween>
                </Box>
            }
            header={localization.DOWNLOAD_TITLE}
        >
            <RadioGroup
                value={selectedOption}
                items={downloadOptions}
                onChange={({ detail }) => setSelectedOption(detail.value)}
            />
        </Modal>
    );
};
export default DownloadModal;
